import { useCallback, useEffect, useState } from 'react';
import { migrateLocalLibrary } from './migrateLocal';
import { addFile, deleteFile, listFiles, type NoticeFileMeta } from './noticeStore';

// Reactive view over the shared file library, used by the viewer and the History so
// both stay in sync after an upload or delete. `files` is null while the first read is
// in flight.
export function useNoticeFiles() {
  const [files, setFiles] = useState<NoticeFileMeta[] | null>(null);

  const refresh = useCallback(async () => {
    setFiles(await listFiles());
  }, []);

  useEffect(() => {
    let alive = true;
    (async () => {
      // Before the first listing, once per browser: push whatever this machine still
      // holds from when the library was local. It has to happen here because it is the
      // first thing that reads the library, and it has to happen before the read or
      // the user watches their own manuals appear a second later.
      //
      // A de-dup pass used to sit here instead — one per device, collapsing copies of
      // one PDF uploaded under a random id each time. The server has no such duplicates
      // to collapse: the id IS the hash, so a second upload is an UPDATE.
      await migrateLocalLibrary();
      if (alive) await refresh();
    })();
    return () => {
      alive = false;
    };
  }, [refresh]);

  const add = useCallback(
    async (file: File) => {
      const rec = await addFile(file);
      await refresh();
      return rec;
    },
    [refresh],
  );

  const remove = useCallback(
    async (id: string) => {
      await deleteFile(id);
      await refresh();
    },
    [refresh],
  );

  return { files, add, remove, refresh };
}
